import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/authUtils';

function VersionsOverview() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filterComponent, setFilterComponent] = useState('all');
  const [filterEnvironment, setFilterEnvironment] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchClientsData();
  }, []);

  const fetchClientsData = async () => {
    try {
      const response = await fetchWithAuth('/api/clients');
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors du chargement des clients');
      }
      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Erreur:', error);
      // Si l'erreur est liée à l'authentification, on pourrait rediriger vers la page de connexion
      if (error.message?.includes('authentification') || error.message?.includes('Token')) {
        // La redirection sera gérée par AuthContext
        console.error('Problème d\'authentification détecté');
      }
    } finally {
      setLoading(false);
    }
  };

  const getVersionsFromServers = (client) => {
    const versions = {
      patient_app: null,
      manager: null,
      marshaller: null,
      marshaller_interop: null
    };

    if (client.servers && client.servers.length > 0) {
      client.servers.forEach(server => {
        // Les versions sont stockées directement dans le serveur avec le préfixe "git"
        if (!versions.patient_app && server.gitPatientAppVersion) {
          versions.patient_app = server.gitPatientAppVersion;
        }
        if (!versions.manager && server.gitManagerFrontAppVersion) {
          versions.manager = server.gitManagerFrontAppVersion;
        }
        if (!versions.marshaller && server.gitMarshallerVersion) {
          versions.marshaller = server.gitMarshallerVersion;
        }
        if (!versions.marshaller_interop && server.gitMarshallerInteropVersion) {
          versions.marshaller_interop = server.gitMarshallerInteropVersion;
        }
      });
    }

    // Nettoyer les versions (enlever le 'v' au début si présent)
    Object.keys(versions).forEach(key => {
      if (versions[key] && versions[key].startsWith('v')) {
        versions[key] = versions[key].substring(1);
      }
    });

    return versions;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const compareVersions = (a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    
    const partsA = a.split('.').map(Number);
    const partsB = b.split('.').map(Number);
    
    for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
      const partA = partsA[i] || 0;
      const partB = partsB[i] || 0;
      if (partA < partB) return -1;
      if (partA > partB) return 1;
    }
    return 0;
  };

  const sortedClients = [...clients].sort((a, b) => {
    const versionsA = getVersionsFromServers(a);
    const versionsB = getVersionsFromServers(b);

    let comparison = 0;
    switch (sortConfig.key) {
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'patient_app':
        comparison = compareVersions(versionsA.patient_app, versionsB.patient_app);
        break;
      case 'manager':
        comparison = compareVersions(versionsA.manager, versionsB.manager);
        break;
      case 'marshaller':
        comparison = compareVersions(versionsA.marshaller, versionsB.marshaller);
        break;
      case 'marshaller_interop':
        comparison = compareVersions(versionsA.marshaller_interop, versionsB.marshaller_interop);
        break;
      default:
        comparison = 0;
    }

    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  const filteredClients = sortedClients.filter(client => {
    const matchesSearch = client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.code?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    // Filtre par environnement
    if (filterEnvironment !== 'all') {
      const clientEnv = client.environment?.toLowerCase() || '';
      const isQual = client.name?.toLowerCase().includes('qual') || clientEnv.includes('qualification');
      const isProd = !isQual && (clientEnv.includes('production') || clientEnv === '');
      
      if (filterEnvironment === 'production' && !isProd) return false;
      if (filterEnvironment === 'qualification' && !isQual) return false;
    }

    // Filtre par composant
    if (filterComponent === 'all') return true;

    const versions = getVersionsFromServers(client);
    switch (filterComponent) {
      case 'patient_app':
        return versions.patient_app;
      case 'manager':
        return versions.manager;
      case 'marshaller':
        return versions.marshaller;
      case 'marshaller_interop':
        return versions.marshaller_interop;
      default:
        return true;
    }
  });

  const exportToCSV = () => {
    const headers = ['Client', 'Code', 'Patient App', 'Manager', 'Marshaller', 'Marshaller Interop'];
    const rows = filteredClients.map(client => {
      const versions = getVersionsFromServers(client);
      return [
        client.name || '',
        client.code || '',
        versions.patient_app || 'N/A',
        versions.manager || 'N/A',
        versions.marshaller || 'N/A',
        versions.marshaller_interop || 'N/A'
      ];
    });

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `versions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center space-x-1 hover:text-blue-600"
    >
      <span>{children}</span>
      {sortConfig.key === column && (
        sortConfig.direction === 'asc' ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )
      )}
    </button>
  );

  const VersionBadge = ({ version, type }) => {
    if (!version) {
      return <span className="text-gray-400 text-sm">N/A</span>;
    }

    // Couleur basée sur le type de composant
    const getColorClass = () => {
      switch (type) {
        case 'patient_app':
          return 'bg-blue-100 text-blue-800';
        case 'manager':
          return 'bg-green-100 text-green-800';
        case 'marshaller':
          return 'bg-purple-100 text-purple-800';
        case 'marshaller_interop':
          return 'bg-orange-100 text-orange-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };

    return (
      <span className={`px-2 py-1 rounded-md text-sm font-medium ${getColorClass()}`}>
        {version}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Chargement des versions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <svg className="mr-3 h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Vue d'ensemble des versions
          </h1>
          <p className="text-gray-600 mt-2">
            Tableau récapitulatif des versions installées pour tous les clients
          </p>
        </div>
        <button
          onClick={exportToCSV}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center"
        >
          <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
          Exporter CSV
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Rechercher par nom ou code client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <select
                value={filterEnvironment}
                onChange={(e) => setFilterEnvironment(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tous les environnements</option>
                <option value="production">Production</option>
                <option value="qualification">Qualification</option>
              </select>
              <select
                value={filterComponent}
                onChange={(e) => setFilterComponent(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Tous les composants</option>
                <option value="patient_app">Avec Patient App</option>
                <option value="manager">Avec Manager</option>
                <option value="marshaller">Avec Marshaller</option>
                <option value="marshaller_interop">Avec Marshaller Interop</option>
              </select>
            </div>
          </div>
          
          <div className="text-sm text-gray-500">
            {filteredClients.length} client{filteredClients.length > 1 ? 's' : ''} affiché{filteredClients.length > 1 ? 's' : ''}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton column="name">Client</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton column="patient_app">Patient App</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton column="manager">Manager</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton column="marshaller">Marshaller</SortButton>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <SortButton column="marshaller_interop">Marshaller Interop</SortButton>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.map((client) => {
                const versions = getVersionsFromServers(client);
                return (
                  <tr
                    key={client.id}
                    onClick={() => navigate(`/client/${client.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {client.name || 'Sans nom'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(() => {
                          const isQual = client.name?.toLowerCase().includes('qual') || 
                                        client.environment?.toLowerCase().includes('qualification');
                          const envText = isQual ? 'Qualification' : 'Production';
                          const envClass = isQual ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
                          
                          return (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${envClass}`}>
                              {envText}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {client.code || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <VersionBadge version={versions.patient_app} type="patient_app" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <VersionBadge version={versions.manager} type="manager" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <VersionBadge version={versions.marshaller} type="marshaller" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <VersionBadge version={versions.marshaller_interop} type="marshaller_interop" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredClients.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucun client trouvé avec les critères sélectionnés
          </div>
        )}
      </div>

      {/* Section de statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {['patient_app', 'manager', 'marshaller', 'marshaller_interop'].map(component => {
          const componentClients = clients.filter(client => {
            const versions = getVersionsFromServers(client);
            return versions[component];
          });

          const componentLabel = {
            patient_app: 'Patient App',
            manager: 'Manager',
            marshaller: 'Marshaller',
            marshaller_interop: 'Marshaller Interop'
          }[component];

          const bgColor = {
            patient_app: 'bg-blue-50 border-blue-200',
            manager: 'bg-green-50 border-green-200',
            marshaller: 'bg-purple-50 border-purple-200',
            marshaller_interop: 'bg-orange-50 border-orange-200'
          }[component];

          return (
            <div key={component} className={`${bgColor} border rounded-lg p-4`}>
              <h3 className="font-medium text-gray-700 mb-2">{componentLabel}</h3>
              <div className="text-2xl font-bold text-gray-900">
                {componentClients.length}
              </div>
              <div className="text-sm text-gray-600">
                clients avec ce composant
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default VersionsOverview;
